import { PlaceType, RoomType } from 'src/types';

import { client } from './client';
import { EmployeeShortInfo, SpaceInfo, StatusResponse } from './types';

export interface DeskPlaceShort {
  placeId: number;
  spaceId: number | null;
  planGeometry: string;
  title: string;
  typeId: PlaceType.Desk;
  responsible: EmployeeShortInfo | null;
}

export interface RoomPlaceShort {
  placeId: number;
  spaceId: number | null;
  planGeometry: string;
  title: string;
  typeId: PlaceType.Room;
  roomTypeId: RoomType;
  seatCount: number;
}

export interface ParkingPlaceShort {
  placeId: number;
  spaceId: number | null;
  planGeometry: string;
  title: string;
  typeId: PlaceType.Parking;
  responsible: EmployeeShortInfo | null;
  guest: boolean;
}

export type PlaceShort = DeskPlaceShort | RoomPlaceShort | ParkingPlaceShort;

export interface DeskPlaceFull {
  placeId: number;
  title: string;
  typeId: PlaceType.Desk;
  byWindow: boolean;
  withCharger: boolean;
  withDivider: boolean;
  withMonitor: boolean;
  responsible: EmployeeShortInfo | null;
}

export interface RoomPlaceFull {
  placeId: number;
  title: string;
  typeId: PlaceType.Room;
  roomTypeId: RoomType;
  square: number;
  seatCount: number;
  withTv: boolean;
  withVideoCalls: boolean;
  withFlipChart: boolean;
  withWater: boolean;
}

export interface ParkingPlaceFull {
  placeId: number;
  spaceId: number | null;
  planGeometry: string;
  title: string;
  typeId: PlaceType.Parking;
  responsible: EmployeeShortInfo | null;
  guest: boolean;
}

export type PlaceFull = DeskPlaceFull | RoomPlaceFull | ParkingPlaceFull;

export interface ReadAllPlacesAtPlanResponse {
  places: PlaceShort[];
  relatedSpaces: SpaceInfo[];
}

export const readAllPlacesAtPlan = async (
  floorId: number,
): Promise<ReadAllPlacesAtPlanResponse> => {
  try {
    const response = await client.get<ReadAllPlacesAtPlanResponse>(
      `/api/v2/places/allAtFloor/${floorId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const readDeskPlace = async (
  placeId: number,
): Promise<DeskPlaceFull> => {
  try {
    const response = await client.get<DeskPlaceFull>(
      `/api/v2/places/deskPlace/${placeId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const readRoomPlace = async (
  placeId: number,
): Promise<RoomPlaceFull> => {
  try {
    const response = await client.get<RoomPlaceFull>(
      `/api/v2/places/roomPlace/${placeId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const readParkingPlace = async (
  placeId: number,
): Promise<ParkingPlaceFull> => {
  try {
    const response = await client.get<ParkingPlaceFull>(
      `/api/v2/places/parkingPlace/${placeId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface CreateDeskPlaceParams {
  floorId: number;
  responsible: string | undefined;
  planGeometry: string;
  title: string;
  byWindow: boolean;
  withMonitor: boolean;
  withCharger: boolean;
  withDivider: boolean;
}

export const createDeskPlace = async (
  params: CreateDeskPlaceParams,
): Promise<StatusResponse> => {
  try {
    const response = await client.post<StatusResponse>(
      '/api/v2/places/deskPlace',
      params,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface UpdateDeskPlaceParams {
  placeId: number;
  responsible: string | undefined;
  planGeometry: string;
  title: string;
  byWindow?: boolean;
  withMonitor?: boolean;
  withCharger?: boolean;
  withDivider?: boolean;
}

export const updateDeskPlace = async (
  params: UpdateDeskPlaceParams,
): Promise<StatusResponse> => {
  try {
    const response = await client.put<StatusResponse>(
      '/api/v2/places/deskPlace',
      params,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface CreateRoomPlaceParams {
  floorId: number;
  planGeometry: string;
  title: string;
  roomTypeId: RoomType;
  square: number;
  seatCount: number;
  withTv: boolean;
  withVideoCalls: boolean;
  withFlipChart: boolean;
  withWater: boolean;
  adEmail?: string;
}

export const createRoomPlace = async (
  params: CreateRoomPlaceParams,
): Promise<StatusResponse> => {
  try {
    const response = await client.post<StatusResponse>(
      '/api/v2/places/roomPlace',
      params,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface UpdateRoomPlaceParams {
  placeId: number;
  planGeometry: string;
  title: string;
  roomTypeId: RoomType;
  square?: number;
  seatCount?: number;
  withTv?: boolean;
  withVideoCalls?: boolean;
  withFlipChart?: boolean;
  withWater?: boolean;
  adEmail?: string;
}

export const updateRoomPlace = async (
  params: UpdateRoomPlaceParams,
): Promise<StatusResponse> => {
  try {
    const response = await client.put<StatusResponse>(
      '/api/v2/places/roomPlace',
      params,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface CreateParkingPlaceParams {
  floorId: number;
  responsible: string | undefined;
  planGeometry: string;
  title: string;
  guest: boolean;
}

export const createParkingPlace = async (
  params: CreateParkingPlaceParams,
): Promise<StatusResponse> => {
  try {
    const response = await client.post<StatusResponse>(
      '/api/v2/places/parkingPlace',
      params,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface UpdateParkingPlaceParams {
  placeId: number;
  responsible: string | undefined;
  planGeometry: string;
  title: string;
  guest: boolean;
}

export const updateParkingPlace = async (
  params: UpdateParkingPlaceParams,
): Promise<StatusResponse> => {
  try {
    const response = await client.put<StatusResponse>(
      '/api/v2/places/parkingPlace',
      params,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const deleteDeskPlace = async (
  placeId: number,
): Promise<StatusResponse> => {
  try {
    const response = await client.delete<StatusResponse>(
      `/api/v2/places/deskPlace/${placeId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const deleteRoomPlace = async (
  placeId: number,
): Promise<StatusResponse> => {
  try {
    const response = await client.delete<StatusResponse>(
      `/api/v2/places/roomPlace/${placeId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const deleteParkingPlace = async (
  placeId: number,
): Promise<StatusResponse> => {
  try {
    const response = await client.delete<StatusResponse>(
      `/api/v2/places/parkingPlace/${placeId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface SharedPeriod {
  from: string;
  to: string;
}

export interface PlaceSharing {
  placeId: number;
  sharedFrom: string;
  sharedTo: string;
}

export interface SharePlaceParams {
  placeId: number;
  sharedPeriods: SharedPeriod[];
}

export interface GetSharingsResponse {
  placeSharings: PlaceSharing[];
}

export const sharePlace = async (
  params: SharePlaceParams,
): Promise<StatusResponse> => {
  try {
    const response = await client.put<StatusResponse>(
      `/api/v2/places/sharings`,
      params,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const getSharings = async (placeId: number): Promise<PlaceSharing[]> => {
  try {
    const response = await client.get<GetSharingsResponse>(
      `/api/v2/places/${placeId}/sharings`,
    );
    return response.data.placeSharings;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};
